import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { FiEdit } from "react-icons/fi";
import { MdDelete, MdKeyboardBackspace } from "react-icons/md";
import moment from "moment";
import { TbReceiptRupee } from "react-icons/tb";
import AlertBox from "../../../components/common/AlertBox";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { checkAcessRights } from "../../../_helper/helper";
import {
  closeDialog,
  openDialog,
} from "../../../components/dialog/dialogSlice";
import { clearInquiryID, setInquiryID } from "../../inquiry/inquirySlice";
import IconButton from "../../../components/common/IconButton";
import { LoadingPanel } from "../../../components/layout/Loading";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../../_contstants/common";
import { deleteInquiry } from "../../inquiry/services/inquiry.services";
import AppDialog, {
  DialogCloseButton,
} from "../../../components/dialog/Dialog";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import { getDashboardInquiries } from "../services/dashboard.services";

const DashBoardInquiries: React.FC = () => {
  const navigate = useNavigate();
  return (
    <>
      <DeleteInquiryDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton
              fillMode="solid"
              title="Back"
              onClick={() => navigate(-1)}
              icon={MdKeyboardBackspace}
              iconClassName="absolute-position"
              style={{ height: 30 }}
              iconStyle={{ fontSize: "20px" }}
            />
            <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
              Inquiry List
            </Typography.h4>
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <InquiryGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const InquiryGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isEditBtnRef = useRef<any>(false);
  const isAddAccess = checkAcessRights("/quotation", INSERT_ACCESS);
  const isEditAccess = checkAcessRights("/inquiry", EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights("/inquiry", DELETE_ACCESS);

  const InquiryList = useAppSelector(
    (state) => state.dashboard.DashboardInquiryList
  );
  const loading = useAppSelector((state) => state.dashboard.loading);
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);

  useEffect(() => {
    const payload = {
      FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
      ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
    };
    dispatch(getDashboardInquiries(payload));
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? InquiryList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEditInquiry = (data: any) => {
      navigate("/inquiry/edit", {
        state: { InquiryID: data?.ID, RequirementType: data?.RequirementType },
      });
    };
    const handleAddQuotation = (data: any) => {
      navigate("/quotation/create", {
        state: {
          InquiryID: data?.ID,
        },
      });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteInquiry"));
      dispatch(setInquiryID(ID));
    };
    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.style, right: 0 }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => {
              isEditBtnRef.current = true;
              handleEditInquiry(props.dataItem);
            }}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isAddAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Generate Quotation"
            onClick={() => handleAddQuotation(props.dataItem)}
            icon={TbReceiptRupee}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 9 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const gridRef = useRef<any>(null);
  if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      {InquiryList && InquiryList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 150px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(InquiryList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : InquiryList.slice(page.skip, page.take + page.skip)
          }
          skip={page.skip}
          take={page.take}
          total={InquiryList.length}
          onFilterChange={handleFilterChange}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Name"
            title="Name"
            width={300}
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <>
                    <div
                      className="hoverableName"
                      onClick={() =>
                        navigate("/inquiry/edit", {
                          state: {
                            InquiryID: props.dataItem?.ID,
                            RequirementType: props.dataItem?.RequirementType,
                          },
                        })
                      }
                    >
                      {props.dataItem?.FirstName} {props.dataItem?.LastName}
                    </div>
                    <p className="m-0"> {props.dataItem?.MobileNo}</p>
                    <p className="m-0"> {props.dataItem?.EmailID}</p>
                  </>
                ) : (
                  <div className="normalName">
                    <p className="m-0">
                      {props.dataItem?.FirstName} {props.dataItem?.LastName}
                    </p>
                    <p className="m-0"> {props.dataItem?.MobileNo}</p>
                    <p className="m-0"> {props.dataItem?.EmailID}</p>
                  </div>
                )}
              </td>
            )}
          />
          <Column
            field="InquiryDate"
            title="Inquiry Date"
            width={200}
            cell={(props: any) => {
              const toDay = moment();
              const InquiryDate = moment(
                String(props.dataItem?.InquiryDate),
                "DD/MM/YYYY",
                true
              );
              let remainingDuration = "";

              if (toDay.isValid() && InquiryDate.isValid()) {
                const duration = moment?.duration(toDay?.diff(InquiryDate));
                const durationChunks = {
                  years: duration.years(),
                  months: duration.months(),
                  days: duration.days(),
                };

                remainingDuration = `${
                  durationChunks.years > 0
                    ? `${durationChunks.years} Years and `
                    : ""
                } ${
                  durationChunks.months > 0
                    ? `${durationChunks.months} Months and `
                    : ""
                } ${durationChunks.days} Days`;
              }

              return (
                <td>
                  <p className="m-0">{props.dataItem?.InquiryDate}</p>
                  <p className="m-0">{remainingDuration}</p>
                </td>
              );
            }}
          />
          <Column field="InquirySource" title="Inquiry Source" width={200} />
          <Column field="TravelDate" title="Date" width={150} />
          <Column field="NoOfPax" title="No Of Pax" width={150} />
          <Column
            field="NoOfNights"
            title="No Of Days"
            width={150}
            cell={(props: any) => (
              <td>
                <p>
                  {props.dataItem?.NoOfNights && props.dataItem?.NoOfNights > 0
                    ? +props.dataItem?.NoOfNights + 1
                    : ""}
                </p>
              </td>
            )}
          />
          <Column field="InquiryStage" title="Inquiry Stage" width={200} />
          <Column field="Remarks" title="Remarks" width={300} />
          <Column field="InquiryOwnerName" title="Owner" width={200} />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"140px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteInquiryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const InquiryID = useAppSelector((state) => state.inquiry.InquiryID);
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);

  const handleDeleteAction = async (ID: number | null) => {
    const payload = {
      FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
      ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
    };
    if (ID) {
      const response = await dispatch(deleteInquiry(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getDashboardInquiries(payload));
        dispatch(closeDialog());
        dispatch(clearInquiryID());
      } else {
        dispatch(getDashboardInquiries(payload));
        dispatch(closeDialog());
        dispatch(clearInquiryID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteInquiry" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Inquiry?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Inquiry?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(InquiryID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default DashBoardInquiries;
